import React from 'react'
import { UnderwritingOffer } from '_gql'

import { useCanary } from '@bill/canary.react'
import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import Spinner from '@divvy-web/skylab.spinner'

import BillBrandLogo from '@sputnik/components/BillBrandLogo'
import GoToDashboardButton from '@sputnik/components/GoToDashboardButton'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import CreditLineOffersPage from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage'
import { URL } from '@sputnik/resources/constants'

import EligibleOfferTile from './EligibleOfferTile'
import { useGetEligibleOffers } from './GetEligibleOffers.gql'
import IncludedWithOffers from './IncludedWithOffers'
import styles from './styles.module.scss'

const EligibleOffersPage = () => {
  const [getClassName] = useNameFormatter('EligibleOffersPage')
  const isUWPageRedesign = useCanary('rollout-uw-page-redesign')

  const [companyDivvyUuid] = useDivvyUUID()

  const { data: decisionsData, loading: isLoadingDecisionData } = useGetEligibleOffers({
    skip: !companyDivvyUuid || !isUWPageRedesign,
    variables: { companyId: companyDivvyUuid },
  })

  if (!isUWPageRedesign) {
    return <CreditLineOffersPage />
  }

  const decisions = decisionsData?.decisions || []

  const standardLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.FIXED_LINE)
  const variableLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.VARIABLE_LINE)
  const securedLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.SECURED_LINE)

  const { hasStandardOffer, hasVariableOffer, hasSecuredOffer } = {
    hasStandardOffer: standardLineOffer !== undefined,
    hasVariableOffer: variableLineOffer !== undefined,
    hasSecuredOffer: securedLineOffer !== undefined,
  }

  const offerCount = [hasStandardOffer, hasVariableOffer, hasSecuredOffer].filter(Boolean).length

  if (!decisionsData || isLoadingDecisionData) {
    return (
      <div>
        <Spinner centered />
      </div>
    )
  }

  return (
    <div className={styles[getClassName('')]}>
      <BillBrandLogo />
      <div className={styles[getClassName('content')]}>
        <div className={styles[getClassName('title')]}>
          <FormattedMessage
            defaultMessage='Congrats, you are eligible for {numOffers, plural, one {# offer} other {# offers}}.'
            id='sputnik.EligibleOffersPage__/q5IXg'
            values={{ numOffers: offerCount }}
          />
        </div>
        <div className={styles[getClassName('subtitle')]}>
          <FormattedMessage
            defaultMessage='Based on the information provided from you application.'
            id='sputnik.EligibleOffersPage__bpaGwn'
          />
        </div>
        <div className={styles[getClassName('tiles')]}>
          {hasVariableOffer && (
            <EligibleOfferTile
              approvedAmount={variableLineOffer?.approvedAmountInCents}
              bulletPoints={[
                <FormattedMessage
                  key='credit-limit-fluctuate'
                  defaultMessage='Your credit limit may fluctuate based on the cash balance in your connected bank accounts'
                  id='sputnik.EligibleOffersPage__GiJHAo'
                />,
                <FormattedMessage
                  key='secure-digital-banking'
                  defaultMessage="We'll use a secure digital banking connection to review your account balances and determine any changes to your credit limit"
                  id='sputnik.EligibleOffersPage__3/EfUe'
                />,
                <FormattedMessage
                  key='balance-due-in-full'
                  defaultMessage='Your balance is due in full at the end of each billing cycle'
                  id='sputnik.EligibleOffersPage__lysSKm'
                />,
              ]}
              description={
                <FormattedMessage
                  defaultMessage='For high-growth businesses that need a limit that scales with their cash flow'
                  id='sputnik.EligibleOffersPage__229Q7f'
                />
              }
              learnMoreLink={URL.VARIABLE_LINE_OFFER_LEARN_MORE_LINK}
              title={
                <FormattedMessage
                  defaultMessage='Dynamic cash-based line'
                  id='sputnik.EligibleOffersPage__rkSl0s'
                />
              }
              // this will open the tila modal
              onViewOfferDetailsClick={() => {}}
            />
          )}
          {hasStandardOffer && (
            <EligibleOfferTile
              isMostPopular
              approvedAmount={standardLineOffer?.approvedAmountInCents}
              bulletPoints={[
                <FormattedMessage
                  key='fixed-limit'
                  defaultMessage='Fixed limit that builds a long-term credit relationship over time'
                  id='sputnik.EligibleOffersPage__T4ZCvI'
                />,
                <FormattedMessage
                  key='keep-cash'
                  defaultMessage='Keep cash in your bank — pay on a schedule, not upfront'
                  id='sputnik.EligibleOffersPage__Gxov4L'
                />,
                <FormattedMessage
                  key='balance-due'
                  defaultMessage='Balance due in full at the end of each billing cycle'
                  id='sputnik.EligibleOffersPage__x8jXFJ'
                />,
              ]}
              description={
                <FormattedMessage
                  defaultMessage='For established businesses that want predictable, steady access to credit'
                  id='sputnik.EligibleOffersPage__ZRWHX2'
                />
              }
              learnMoreLink={URL.STANDARD_LINE_OFFER_LEARN_MORE_LINK}
              title={
                <FormattedMessage
                  defaultMessage='Fixed credit line'
                  id='sputnik.EligibleOffersPage__oWLr0e'
                />
              }
              // this will open the tila modal
              onViewOfferDetailsClick={() => {}}
            />
          )}
          {hasSecuredOffer && (
            <EligibleOfferTile
              bulletPoints={[
                <FormattedMessage
                  key='spend-only'
                  defaultMessage="Spend only what you've prefunded — no overspending or debt risk"
                  id='sputnik.EligibleOffersPage__q8+k3I'
                />,
                <FormattedMessage
                  key='same-budget'
                  defaultMessage='Same budgets, policies, and controls as credit-line customers'
                  id='sputnik.EligibleOffersPage__BcCU5Q'
                />,
                <FormattedMessage
                  key='earn-minimum'
                  defaultMessage='Earn minimum 1.5x points on every dollar — no minimums or restrictions'
                  id='sputnik.EligibleOffersPage__bxZUyw'
                />,
              ]}
              description={
                <FormattedMessage
                  defaultMessage='For new businesses building credit or companies maximizing rewards'
                  id='sputnik.EligibleOffersPage__Qdr3fF'
                />
              }
              learnMoreLink={URL.PREPAY_OFFER_LEARN_MORE_LINK}
              subtitle={
                <FormattedMessage
                  defaultMessage='You choose the credit limit'
                  id='sputnik.EligibleOffersPage__JxIS4R'
                />
              }
              title={
                <FormattedMessage
                  defaultMessage='Pre-fund'
                  id='sputnik.EligibleOffersPage__yXk7XL'
                />
              }
              // this will open the tila modal
              onViewOfferDetailsClick={() => {}}
            />
          )}
        </div>
        <IncludedWithOffers />
        {/* COM-10324: insert application agreement block logic here */}
        <div className={styles[getClassName('footer')]}>
          <GoToDashboardButton
            buttonType={BASIC_BUTTON_TYPE_OUTLINED}
            componentName={getClassName('')}
          />
          {/* COM-10324: add logic to hide this button until they have selected an offer and if !isLoggedInUserAuthorizedSigner */}
          <div>
            <BasicButton
              disabled={false}
              showSpinner={false}
              onClick={() => {}}
            >
              <FormattedMessage
                defaultMessage='Accept offer'
                id='sputnik.EligibleOffersPage__/xro5W'
              />
            </BasicButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EligibleOffersPage
